import { Suspense, useId, useRef, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { twJoin } from "tailwind-merge";

import type { FriendGroup, Profile } from "@/api/bale.zod";

import {
  useAddFriendGroupMember,
  useGetFriendGroup,
  useListFriendGroups,
  useListFriendsSuspense,
  useRemoveFriend,
  useRemoveFriendGroupMember,
  useUpdateFriend,
} from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Card, CardActionsHeader } from "@/components/Card";
import { Dropdown } from "@/components/Dropdown";
import { Form } from "@/components/Form";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";
import { MenuField } from "@/components/MenuField";
import { MenuItem } from "@/components/MenuItem";
import { Missing } from "@/components/Missing";
import { ProfileItem } from "@/components/ProfileItem";
import { TextLoading } from "@/components/TextLoading";

export function Friends({ onAddFriend }: { onAddFriend: () => void }) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardActionsHeader title={t("friends")}>
        <IconButton type="button" name="user-plus" onClick={onAddFriend} />
      </CardActionsHeader>
      <Suspense
        fallback={
          <div className="flex flex-col gap-2 px-4 pb-2">
            <TextLoading className="h-12 w-full" />
            <TextLoading className="h-12 w-full" />
            <TextLoading className="h-12 w-full" />
          </div>
        }
      >
        <FriendsContents />
      </Suspense>
    </Card>
  );
}

function FriendsContents() {
  const { t } = useTranslation();
  const friends = useListFriendsSuspense("@me", {
    query: { select: (data) => data.data.friends },
  });

  return (
    // oxlint-disable-next-line explicit-length-check -- messes up my syntax highlighting
    <div className={twJoin("flex flex-col gap-2", !!friends.data.length && "mb-2")}>
      {friends.data.map((friend) => (
        <Friend key={friend.profile.id} note={friend.note} profile={friend.profile} />
      ))}
      {friends.data.length === 0 && <Missing className="mx-4 mb-4">{t("no-friends")}</Missing>}
    </div>
  );
}

function Friend({ profile, note }: { profile: Profile; note?: string | null }) {
  const { t } = useTranslation();
  const updateFriend = useUpdateFriend();
  const removeFriend = useRemoveFriend();

  const listFriendGroups = useListFriendGroups({
    query: { select: (data) => data.data.friend_groups },
  });

  const id = useId();
  const subId = useId();

  const [isEditingNote, setIsEditingNote] = useState(false);
  const popover = useRef<HTMLDivElement>(null);

  function saveNote(event: SubmitEvent<HTMLFormElement>) {
    const form = new FormData(event.currentTarget);
    const note = form.get("note")! as string;
    updateFriend.mutate({ friendId: profile.id, data: { note } });
    popover.current?.hidePopover();
  }

  return (
    <div className="relative flex flex-row justify-between gap-4 pr-2 pl-4">
      <ProfileItem profile={profile} note={note} />
      <ActionBox>
        <IconButton type="button" name="menu" popoverTarget={id} />
      </ActionBox>

      <Dropdown
        popover="auto"
        id={id}
        className="absolute ml-2 w-max [position-area:right_span-bottom]"
        onToggle={() => setIsEditingNote(false)}
        ref={popover}
      >
        <MenuItem to={`/profile/${profile.id}`}>
          <Icon name="smile" /> {t("view-profile")}
        </MenuItem>
        {isEditingNote ? (
          <MenuField>
            <Icon name="edit" />
            <Form error={updateFriend.error} onSubmit={saveNote}>
              <Input type="text" name="note" placeholder={t("friend-note-placeholder")} autoFocus />
            </Form>
          </MenuField>
        ) : (
          <MenuItem onClick={() => setIsEditingNote(true)}>
            <Icon name="edit" /> {t("edit-note")}
          </MenuItem>
        )}
        <MenuItem popoverTarget={subId}>
          <Icon name="list" />
          <span className="mr-4">{t("manage-groups")}</span>
          <Icon className="ml-auto" name="chevron-right" />
        </MenuItem>
        <MenuItem onClick={() => removeFriend.mutate({ friendId: profile.id })}>
          <Icon name="user-x" /> {t("remove-friend")}
        </MenuItem>

        <Dropdown popover="auto" id={subId} className="absolute -ml-2 w-max [position-area:right_span-bottom]">
          {listFriendGroups.data?.map((group) => (
            <ToggleFriendGroupButton key={group.id} friendId={profile.id} friendGroup={group} />
          ))}
        </Dropdown>
      </Dropdown>
    </div>
  );
}

function ToggleFriendGroupButton({ friendId, friendGroup }: { friendId: string; friendGroup: FriendGroup }) {
  const isMember = useGetFriendGroup(friendGroup.id, {
    query: { select: (data) => data.data.members.some((member) => member.profile.id === friendId) },
  });
  const addFriendGroupMember = useAddFriendGroupMember();
  const removeFriendGroupMember = useRemoveFriendGroupMember();

  function toggleMembership() {
    if (isMember.data!) {
      removeFriendGroupMember.mutate({ friendGroupId: friendGroup.id, data: { friend_id: friendId } });
    } else {
      addFriendGroupMember.mutate({ friendGroupId: friendGroup.id, data: { friend_id: friendId } });
    }
  }

  return (
    <MenuItem onClick={toggleMembership} disabled={isMember.isLoading}>
      <Icon name="check" className={isMember.data ? undefined : "invisible"} />
      {friendGroup.name}
    </MenuItem>
  );
}
